/**
 * SearchForm Component
 * Professional search form with state/profession dropdowns and zip code input
 */

import React, { useState, useEffect } from 'react';
import { useSources } from '../hooks/useApi';
import type { SearchParams, Source } from './types/api.types';
import Button from './ui/Button';

interface SearchFormProps {
  onSearch: (params: SearchParams) => void;
  isLoading: boolean;
  rateLimitInfo?: {
    searches_used: number;
    searches_limit: number;
  };
}

const SearchForm: React.FC<SearchFormProps> = ({
  onSearch,
  isLoading,
  rateLimitInfo,
}) => {
  const { data: sourcesData, isLoading: sourcesLoading } = useSources();

  const [state, setState] = useState('');
  const [profession, setProfession] = useState('');
  const [zip, setZip] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Available professions for selected state
  const [availableProfessions, setAvailableProfessions] = useState<string[]>(
    []
  );

  // Update professions when state changes
  useEffect(() => {
    if (state && sourcesData?.sources) {
      const selectedSource = sourcesData.sources.find(
        (s: Source) => s.state === state
      );
      setAvailableProfessions(selectedSource?.professions || []);
      // Reset profession if not available in new state
      if (
        profession &&
        !selectedSource?.professions.includes(profession)
      ) {
        setProfession('');
      }
    } else {
      setAvailableProfessions([]);
    }
  }, [state, sourcesData, profession]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!state) {
      newErrors.state = 'Please select a state';
    }

    if (!profession) {
      newErrors.profession = 'Please select a profession';
    }

    if (zip && !/^\d{5}$/.test(zip)) {
      newErrors.zip = 'ZIP code must be 5 digits';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const params: SearchParams = {
      state,
      profession,
      status: 'all',
      limit: 100,
    };

    if (zip) {
      params.zip = zip;
    }

    onSearch(params);
  };

  const searchesRemaining = rateLimitInfo
    ? rateLimitInfo.searches_limit - rateLimitInfo.searches_used
    : null;

  return (
    <form
      onSubmit={handleSubmit}
      className="bg-slate-800 border border-slate-700 rounded-lg p-6 space-y-6"
    >
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* State Dropdown */}
        <div className="space-y-2">
          <label
            htmlFor="state"
            className="block text-sm font-medium text-slate-300"
          >
            State <span className="text-red-500">*</span>
          </label>
          <select
            id="state"
            value={state}
            onChange={(e) => setState(e.target.value)}
            disabled={sourcesLoading || isLoading}
            className={`w-full px-4 py-3 bg-slate-900 border ${
              errors.state ? 'border-red-500' : 'border-slate-700'
            } rounded-lg text-slate-200 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            <option value="">Select state...</option>
            {sourcesData?.sources.map((source: Source) => (
              <option key={source.state} value={source.state}>
                {source.state_name} ({source.state})
              </option>
            ))}
          </select>
          {errors.state && (
            <p className="text-sm text-red-500">{errors.state}</p>
          )}
        </div>

        {/* Profession Dropdown */}
        <div className="space-y-2">
          <label
            htmlFor="profession"
            className="block text-sm font-medium text-slate-300"
          >
            Profession <span className="text-red-500">*</span>
          </label>
          <select
            id="profession"
            value={profession}
            onChange={(e) => setProfession(e.target.value)}
            disabled={!state || isLoading}
            className={`w-full px-4 py-3 bg-slate-900 border ${
              errors.profession ? 'border-red-500' : 'border-slate-700'
            } rounded-lg text-slate-200 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            <option value="">Select profession...</option>
            {availableProfessions.map((prof) => (
              <option key={prof} value={prof}>
                {prof.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
              </option>
            ))}
          </select>
          {errors.profession && (
            <p className="text-sm text-red-500">{errors.profession}</p>
          )}
        </div>

        {/* ZIP Code Input */}
        <div className="space-y-2">
          <label
            htmlFor="zip"
            className="block text-sm font-medium text-slate-300"
          >
            ZIP Code (optional)
          </label>
          <input
            type="text"
            id="zip"
            value={zip}
            onChange={(e) => setZip(e.target.value.replace(/\D/g, '').slice(0, 5))}
            placeholder="12345"
            maxLength={5}
            disabled={isLoading}
            className={`w-full px-4 py-3 bg-slate-900 border ${
              errors.zip ? 'border-red-500' : 'border-slate-700'
            } rounded-lg text-slate-200 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed`}
          />
          {errors.zip && <p className="text-sm text-red-500">{errors.zip}</p>}
        </div>
      </div>

      {/* Rate Limit Indicator */}
      {searchesRemaining !== null && (
        <div className="flex items-center justify-between bg-slate-900 border border-slate-700 rounded-lg px-4 py-3">
          <div className="flex items-center space-x-3">
            <div className="flex-shrink-0">
              <svg
                className="w-5 h-5 text-sky-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <div>
              <p className="text-sm text-slate-300">
                <span className="font-bold text-sky-400">
                  {searchesRemaining}
                </span>{' '}
                of {rateLimitInfo?.searches_limit} free searches remaining
              </p>
            </div>
          </div>
          <div className="w-32 bg-slate-800 rounded-full h-2">
            <div
              className={`h-2 rounded-full transition-all ${
                searchesRemaining <= 3
                  ? 'bg-red-500'
                  : searchesRemaining <= 5
                  ? 'bg-yellow-500'
                  : 'bg-sky-500'
              }`}
              style={{
                width: `${
                  rateLimitInfo
                    ? (searchesRemaining / rateLimitInfo.searches_limit) * 100
                    : 0
                }%`,
              }}
            />
          </div>
        </div>
      )}

      {/* Submit Button */}
      <div className="flex justify-end">
        <Button
          type="submit"
          variant="primary"
          size="lg"
          disabled={isLoading || sourcesLoading}
          className="min-w-[200px]"
        >
          {isLoading ? (
            <span className="flex items-center">
              <svg
                className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              Searching...
            </span>
          ) : (
            'Search Professionals'
          )}
        </Button>
      </div>
    </form>
  );
};

export default SearchForm;
